import { postAnalyticsEvents, type TelemetryEventPayload } from '@/api/endpoints/telemetry';

const FLUSH_INTERVAL_MS = 5_000;
const MAX_BATCH_SIZE = 20;
const MAX_QUEUE_SIZE = 200;
const MAX_RETRY_ATTEMPTS = 3;

export type AnalyticsUserContext = {
  userId?: number;
  email?: string;
  companyId?: number;
};

type QueuedEvent = TelemetryEventPayload & { attempts: number };

let userContext: AnalyticsUserContext = {};
let queue: QueuedEvent[] = [];
let flushTimer: ReturnType<typeof setTimeout> | null = null;
let initialized = false;

function scheduleFlush(): void {
  if (flushTimer != null) return;
  flushTimer = setTimeout(() => {
    flushTimer = null;
    void flushQueue();
  }, FLUSH_INTERVAL_MS);
}

function enqueue(event: TelemetryEventPayload): void {
  if (queue.length >= MAX_QUEUE_SIZE) {
    queue.shift();
  }
  queue.push({ ...event, attempts: 0 });
  if (queue.length >= MAX_BATCH_SIZE) {
    void flushQueue();
  } else {
    scheduleFlush();
  }
}

/**
 * Sends queued events to `POST /api/analytics/events`. Re-queues on
 * transient failure up to {@link MAX_RETRY_ATTEMPTS}.
 */
export async function flushQueue(): Promise<void> {
  if (queue.length === 0) return;
  if (typeof navigator !== 'undefined' && !navigator.onLine) return;

  const batch = queue.splice(0, MAX_BATCH_SIZE);
  try {
    await postAnalyticsEvents(
      batch.map(({ attempts: _attempts, ...event }) => event),
    );
  } catch {
    for (const item of batch) {
      if (item.attempts < MAX_RETRY_ATTEMPTS) {
        queue.unshift({ ...item, attempts: item.attempts + 1 });
      }
    }
    scheduleFlush();
  }
}

/** Wire user context once per authenticated session. */
export function setAnalyticsUserContext(user?: AnalyticsUserContext): void {
  if (user) {
    userContext = {
      userId: user.userId,
      email: user.email,
      companyId: user.companyId,
    };
  }
}

/** Mark the sink ready — idempotent. */
export function initAnalyticsSink(user?: AnalyticsUserContext): void {
  if (initialized) {
    if (user) setAnalyticsUserContext(user);
    return;
  }
  initialized = true;
  if (user) setAnalyticsUserContext(user);

  if (typeof window !== 'undefined') {
    window.addEventListener('online', () => {
      void flushQueue();
    });
    window.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'hidden') {
        void flushQueue();
      }
    });
  }
}

/** Track a named event — enriches with user context and current path. */
export function sendAnalyticsEvent(
  name: string,
  properties?: Record<string, unknown>,
): void {
  const eventPage =
    typeof window !== 'undefined' ? window.location.pathname : undefined;

  const event: TelemetryEventPayload = {
    name,
    properties: { ...userContext, ...properties },
    timestamp: new Date().toISOString(),
    eventPage,
  };

  if (import.meta.env.DEV) {
    // eslint-disable-next-line no-console -- dev-only telemetry echo per analytics contract
    console.debug('[analytics]', event.name, event.properties);
  }

  enqueue(event);
}

/** Drain the queue — exposed for tests and app teardown. */
export function resetAnalyticsSinkForTests(): void {
  queue = [];
  userContext = {};
  initialized = false;
  if (flushTimer != null) {
    clearTimeout(flushTimer);
    flushTimer = null;
  }
}

/** @internal Test hook — current queue depth. */
export function getAnalyticsQueueDepthForTests(): number {
  return queue.length;
}
